import React, { useRef, useState, type ReactNode } from "react";

export type KpiTone = "good" | "bad" | "flat";

export interface MobileKpi {
  id: string;
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  delta?: { text: string; tone: KpiTone };
  spark?: { points: string; tone: "blue" | "gray" | "red" };
  critical?: boolean;
  cta?: { label: string; onClick: () => void };
}

/** Carrusel con encaje al deslizar, la tarjeta siguiente asomando y puntos de posición. */
export function Rail({ label, children }: { label: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [idx, setIdx] = useState(0);
  const items = React.Children.toArray(children);

  const onScroll = () => {
    const el = ref.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (!el || !first) return;
    setIdx(Math.round(el.scrollLeft / (first.offsetWidth + 10)));
  };

  return (
    <div className="rz-railwrap">
      <div className="rz-rail" ref={ref} onScroll={onScroll} role="group" aria-label={label}>
        {items.map((c, i) => (
          <div className="rz-slide" key={i}>
            {c}
          </div>
        ))}
      </div>
      {items.length > 1 && (
        <div className="rz-dots" aria-hidden="true">
          {items.map((_, i) => (
            <i key={i} className={i === idx ? "on" : undefined} />
          ))}
        </div>
      )}
    </div>
  );
}

export function KpiCard({ k }: { k: MobileKpi }) {
  return (
    <article className={`rz-kpi${k.critical ? " critical" : ""}`}>
      <span className="lab">{k.label}</span>
      <div className="val">
        <strong>{k.value}</strong>
        {k.unit && <span>{k.unit}</span>}
      </div>
      {k.delta && <span className={`delta ${k.delta.tone}`}>{k.delta.text}</span>}
      {k.spark && (
        <svg viewBox="0 0 200 36" className={`sbx-sparkline ${k.spark.tone}`} aria-hidden="true">
          <polyline points={k.spark.points} />
        </svg>
      )}
      {k.sub && <span className="sub">{k.sub}</span>}
      {k.cta && (
        <button type="button" onClick={k.cta.onClick}>
          {k.cta.label}
        </button>
      )}
    </article>
  );
}

export function KpiRail({ kpis }: { kpis: MobileKpi[] }) {
  return (
    <Rail label="Indicadores principales">
      {kpis.map((k) => (
        <KpiCard key={k.id} k={k} />
      ))}
    </Rail>
  );
}
